// src/components/analytics/IssueClassifierTrainingWidget.js
// FlowSense MOW+ — Enterprise Issue Classifier Training Widget
//
// Features:
//   - Live Model Metrics (Accuracy, Precision, Recall, F1-Score) via ML Gateway
//   - Interactive Training Trigger with real Job Polling & Progress Simulation
//   - Class Distribution Visualization (React ApexCharts)
//   - Staged Model Management (Promote, Compare, & Delete)
//   - Graceful Error Handling, Skeleton Loaders, & Framer Motion Animations
//   - Fully wired to the FlowSense API ecosystem
//
// --- UPDATE (ML Ops) ---------------------------------------------------------------
//   • Training was broken: it posted to '/api/v1/ml/train-issue-classifier' through the api
//     instance whose baseURL already ends in /api/v1 (→ /api/v1/api/v1/… 404), with an EMPTY
//     training_data array, and showed a random "progress" bar. It now starts a real run on
//     the ML control plane (POST /ml-ops/models/ISSUE_CLASSIFIER/train — FlowSense loads the
//     categorised work orders + issues itself) and shows the run's real stage, progress, log
//     and champion/challenger decision (websocket 'ml_events' + polling fallback).
//   • Registry reads, promote and delete go through mlOpsService: promotion is one transaction
//     that also clears is_active on the old champion and flushes the ML engine cache (the
//     separate '/api/v1/ml/cache/reset' call had the same double-prefix bug and is gone).
//   • The 60 s background sync pauses while the tab is hidden; no setState after unmount.
//   • "Explain" button opens FlowSense AI on this model.
// --- END UPDATE ---------------------------------------------------------------------

import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import {
  Box, Flex, VStack, HStack, Text, Heading, Button, Progress, Badge,
  useColorModeValue, useToast, Icon, Stat, StatLabel, StatNumber,
  SimpleGrid, Divider, Table, Thead, Tbody,
  Tr, Th, Td, Skeleton, Alert, AlertIcon, AlertTitle, AlertDescription,
  Tabs, TabList, TabPanels, Tab, TabPanel, Code, IconButton, Tooltip,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  AlertDialog, AlertDialogOverlay, AlertDialogContent, AlertDialogHeader, AlertDialogBody, AlertDialogFooter
} from '@chakra-ui/react';
import { 
  FaBrain, FaPlay, FaCheckCircle, FaHistory, FaExclamationTriangle,
  FaProjectDiagram, FaHourglassHalf, FaChartBar, FaShieldAlt
} from 'react-icons/fa';
import { MdTrendingUp, MdPrecisionManufacturing, MdFactCheck } from 'react-icons/md';
import { InfoIcon, DeleteIcon, ArrowUpIcon } from '@chakra-ui/icons';
import { motion, AnimatePresence } from 'framer-motion';
import ReactApexChart from 'react-apexcharts';

// --- UPDATE (ML Ops)
import mlOpsService from '../../services/mlOpsService';
import { useMlEvents } from '../../hooks/useMlOps';
import { ExplainButton } from '../ml/FlowSenseAIExplainer';
// --- END UPDATE

// ─────────────────────────────────────────────────────────────────────────────
// ANIMATION VARIANTS
// ─────────────────────────────────────────────────────────────────────────────

const FADE_UP = {
  hidden:  { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0,  transition: { duration: 0.26, ease: 'easeOut' } },
  exit:    { opacity: 0, y: -8, transition: { duration: 0.16 } },
};

const STAGGER_CONTAINER = {
  hidden:  {},
  visible: { transition: { staggerChildren: 0.07 } },
};

const LIST_ITEM_VARIANT = {
  hidden:  { opacity: 0, x: -12 },
  visible: { opacity: 1, x: 0,   transition: { duration: 0.22, ease: 'easeOut' } },
  exit:    { opacity: 0, x: 12,  transition: { duration: 0.14 } },
};

const MotionBox = motion(Box);

// ─────────────────────────────────────────────────────────────────────────────
// UTILITIES (ENTERPRISE FIX: Strict Metric Extraction)
// ─────────────────────────────────────────────────────────────────────────────

const safeParseJson = (data, fallback = {}) => {
  if (typeof data === 'object' && data !== null) return data;
  if (typeof data === 'string') {
    try { return typeof JSON.parse(data) === 'object' ? JSON.parse(data) : fallback; } 
    catch (e) { return fallback; }
  }
  return fallback;
};

const extractClassificationMetrics = (modelData) => {
  if (!modelData) return null;
  const pm = safeParseJson(modelData.performance_metrics || modelData);
  
  // Drill into the actual payload. Python sends the deep stats inside 'full_report'
  const classReport = safeParseJson(pm.full_report || pm.classification_report, null);
  
  const accuracy = pm.accuracy ?? pm.global_accuracy ?? (classReport?.accuracy || 0);

  // Extract macro averages directly from the deep report if top-level keys are missing
  const precision = pm.precision ?? pm.macro_precision ?? (classReport?.['macro avg']?.precision || 0);
  const recall    = pm.recall    ?? pm.macro_recall    ?? (classReport?.['macro avg']?.recall || 0);
  const f1Score   = pm.f1_macro  ?? pm.f1_score        ?? (classReport?.['macro avg']?.['f1-score'] || 0);
  
  return { accuracy, precision, recall, f1Score, classReport };
};

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

const MetricCard = memo(({ label, value, helpText, accent = '#805AD5', icon: Ic, isHealthy = true, isLoading = false }) => {
  const bg = useColorModeValue('white', 'gray.800');
  const bd = useColorModeValue('gray.200', 'rgba(255,255,255,0.08)');
  const lbl = useColorModeValue('gray.500', 'gray.400');
  const iconBg = useColorModeValue(`${accent}18`, `${accent}22`);
  const unhealthyColor = useColorModeValue('red.500', 'red.300');

  return (
    <Box bg={bg} borderWidth="1px" borderColor={bd} borderRadius="xl" p={4} position="relative" overflow="hidden" boxShadow="sm" _hover={{ boxShadow: 'md', transform: 'translateY(-2px)', borderColor: accent }} transition="all 0.2s ease">
      <Box position="absolute" top={0} left={0} right={0} h="3px" bg={accent} style={{ boxShadow: `0 0 8px ${accent}66` }} borderTopRadius="xl" />
      {isLoading ? (
        <VStack spacing={2} align="flex-start" pt={1}>
          <Skeleton height="10px" w="60%" />
          <Skeleton height="24px" w="80%" />
          <Skeleton height="8px" w="40%" />
        </VStack>
      ) : (
        <Flex justify="space-between" align="flex-start" pt={1}>
          <VStack align="flex-start" spacing={1} flex={1} overflow="hidden">
            <Text fontSize="10px" fontWeight="800" color={lbl} textTransform="uppercase" letterSpacing="0.1em">{label}</Text>
            <Text fontSize="22px" fontWeight="800" color={isHealthy ? accent : unhealthyColor} lineHeight="1">{value ?? '—'}</Text>
            {helpText && <Text fontSize="9px" color={lbl} letterSpacing="0.06em">{helpText}</Text>}
          </VStack>
          {Ic && (
            <Box p={2} bg={iconBg} borderRadius="lg" flexShrink={0}>
              <Icon as={Ic} color={accent} boxSize={4} />
            </Box>
          )}
        </Flex>
      )}
    </Box>
  );
});

const ModelComparisonModal = ({ isOpen, onClose, productionModel, stagedModel }) => {
  const headerBg   = useColorModeValue('gray.50', 'gray.700');
  const rowHoverBg = useColorModeValue('gray.50', 'rgba(255,255,255,0.03)');
  const bd         = useColorModeValue('gray.200', 'rgba(255,255,255,0.07)');
  const muted      = useColorModeValue('gray.500', 'gray.400');
  
  const headerGradient = useColorModeValue(
    'linear-gradient(135deg, rgba(128,90,213,0.08), rgba(49,130,206,0.06))',
    'linear-gradient(135deg, rgba(128,90,213,0.12), rgba(49,130,206,0.08))'
  );
  const prodBadgeBg = useColorModeValue('green.50','rgba(52,211,153,0.08)');
  const prodBadgeBorder = useColorModeValue('green.200','rgba(52,211,153,0.2)');
  const stagedBadgeBg = useColorModeValue('purple.50','rgba(167,139,250,0.08)');
  const stagedBadgeBorder = useColorModeValue('purple.200','rgba(167,139,250,0.2)');

  const metrics = [
    { key: 'accuracy', label: 'Global Accuracy', icon: MdFactCheck },
    { key: 'precision', label: 'Macro Precision', icon: MdPrecisionManufacturing },
    { key: 'recall', label: 'Macro Recall', icon: FaChartBar },
    { key: 'f1Score', label: 'Macro F1-Score', icon: MdTrendingUp }
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" motionPreset="slideInBottom">
      <ModalOverlay backdropFilter="blur(4px)" bg="rgba(0,0,0,0.5)" />
      <ModalContent borderRadius="2xl" overflow="hidden" boxShadow="0 0 40px rgba(128,90,213,0.2)">
        <ModalHeader bg={headerGradient} borderBottomWidth="1px" borderColor={bd} pb={4}>
          <HStack spacing={3}>
            <Icon as={FaProjectDiagram} color="purple.500" boxSize={5} />
            <VStack spacing={0} align="flex-start">
              <Text fontSize="15px" fontWeight="800">NLP Model Comparison</Text>
              <Text fontSize="10px" color={muted} letterSpacing="0.08em">PRODUCTION vs STAGED</Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py={6}>
          <motion.div variants={STAGGER_CONTAINER} initial="hidden" animate="visible">
            <MotionBox variants={FADE_UP} mb={4}>
              <Flex gap={3} wrap="wrap">
                <Flex align="center" gap={2} px={3} py={1.5} borderRadius="lg" bg={prodBadgeBg} borderWidth="1px" borderColor={prodBadgeBorder}>
                  <Box w="9px" h="9px" borderRadius="full" bg="#34d399" style={{ boxShadow: '0 0 8px #34d399' }} />
                  <Text fontSize="10px" fontWeight="700" color="green.500">PRODUCTION · v{productionModel?.modelVersion ?? '—'}</Text>
                </Flex>
                <Flex align="center" gap={2} px={3} py={1.5} borderRadius="lg" bg={stagedBadgeBg} borderWidth="1px" borderColor={stagedBadgeBorder}>
                  <Box w="9px" h="9px" borderRadius="full" bg="purple.400" />
                  <Text fontSize="10px" fontWeight="700" color="purple.500">STAGED · v{stagedModel?.model_version ?? '—'}</Text>
                </Flex>
              </Flex>
            </MotionBox>

            <MotionBox variants={FADE_UP}>
              <Box borderRadius="xl" borderWidth="1px" borderColor={bd} overflow="hidden">
                <Table variant="simple" size="sm">
                  <Thead bg={headerBg}>
                    <Tr>
                      {['METRIC','PRODUCTION','STAGED','DELTA'].map(h => (
                        <Th key={h} fontSize="9px" letterSpacing="0.12em" color={muted}>{h}</Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {metrics.map((m) => {
                      const prodVal   = productionModel ? productionModel[m.key] : null;
                      const stagedVal = stagedModel ? stagedModel.parsedMetrics[m.key] : null;
                      
                      // For NLP metrics, higher is ALWAYS better
                      let color = 'gray';
                      if (prodVal !== null && stagedVal !== null) {
                        if (stagedVal > prodVal) color = 'green';
                        else if (stagedVal < prodVal) color = 'red';
                      }

                      return (
                        <Tr key={m.key} _hover={{ bg: rowHoverBg }} transition="background 0.15s">
                          <Td>
                            <HStack spacing={2}>
                              <Icon as={m.icon} color="purple.400" boxSize={3} />
                              <Text fontWeight="800" textTransform="uppercase" fontSize="11px">{m.label}</Text>
                            </HStack>
                          </Td>
                          <Td fontSize="11px" fontWeight="600">{prodVal !== null ? `${(prodVal * 100).toFixed(1)}%` : 'N/A'}</Td>
                          <Td fontSize="11px" fontWeight="600">{stagedVal !== null ? `${(stagedVal * 100).toFixed(1)}%` : 'N/A'}</Td>
                          <Td>
                            <Badge colorScheme={color} borderRadius="full" px={2} py={0.5} fontSize="10px" fontWeight="700">
                              {color === 'green' ? '↑ Better' : color === 'red' ? '↓ Worse' : '—'}
                            </Badge>
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>
            </MotionBox>
          </motion.div>
        </ModalBody>

        <ModalFooter borderTopWidth="1px" borderColor={bd}>
          <Button onClick={onClose} fontSize="11px" letterSpacing="0.06em">CLOSE</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN WIDGET COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

const IssueClassifierTrainingWidget = () => {
  // Theme Colors
  const textColor      = useColorModeValue('gray.600', 'gray.300');
  const headingColor   = useColorModeValue('gray.800', 'white');
  const cardBg         = useColorModeValue('white', 'gray.800');
  const borderColor    = useColorModeValue('gray.200', 'gray.600');
  const tabBg          = useColorModeValue('gray.100', 'rgba(255,255,255,0.04)');
  const tabActiveBg    = useColorModeValue('white', 'rgba(255,255,255,0.08)');
  const listItemBg     = useColorModeValue('gray.50', 'gray.700');

  // State
  const [productionMetrics, setProductionMetrics] = useState(null);
  const [stagedModels, setStagedModels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isTraining, setIsTraining] = useState(false);
  const [trainingProgress, setTrainingProgress] = useState(0);
  const [trainingLogs, setTrainingLogs] = useState([]);
  const [isPromoting, setIsPromoting] = useState(null);
  const [modelToDelete, setModelToDelete] = useState(null);
  const [isComparisonOpen, setIsComparisonOpen] = useState(false);
  const [selectedComparison, setSelectedComparison] = useState(null);
  
  const toast = useToast();
  // --- UPDATE (ML Ops)
  const isMounted = useRef(true);
  const activeJobRef = useRef(null);
  useEffect(() => { isMounted.current = true; return () => { isMounted.current = false; }; }, []);
  // --- END UPDATE

  // ─────────────────────────────────────────────────────────────────────────────
  // DATA FETCHING
  // ─────────────────────────────────────────────────────────────────────────────

  const fetchEcosystemData = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setIsLoading(true);
    try {
      // --- UPDATE (ML Ops): registry via the control plane. The detail call returns the
      //     production version with its full per-class report (for the charts).
      const [detail, staged] = await Promise.all([
        mlOpsService.getModelDetail('ISSUE_CLASSIFIER').catch(() => null),
        mlOpsService.listModels({ modelType: 'ISSUE_CLASSIFIER', status: 'staging', limit: 20 }).catch(() => ({ items: [] })),
      ]);
      if (!isMounted.current) return;
      const champion = detail?.champion || null;
      // --- END UPDATE

      if (champion) {
        const parsedMetrics = extractClassificationMetrics(champion.performance_metrics);
        setProductionMetrics({
          modelVersion: champion.model_version,
          lastTrained: champion.activated_at || champion.created_at || new Date().toISOString(),
          ...parsedMetrics
        });
      } else {
        setProductionMetrics(null);
      }

      // 2. Staged challenger models
      const models = staged?.items || [];
      setStagedModels(models.map(m => ({
        ...m,
        parsedMetrics: extractClassificationMetrics(m.performance_metrics)
      })));
    } catch (error) {
      console.error("[IssueClassifier] Failed to sync with ML ecosystem:", error);
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEcosystemData();
    // Background sync every 60s (skipped while the tab is hidden)
    const interval = setInterval(() => { if (!document.hidden) fetchEcosystemData({ quiet: true }); }, 60000);
    return () => clearInterval(interval);
  }, [fetchEcosystemData]);

  // --- UPDATE (ML Ops): live progress of this model's training runs
  useMlEvents(useCallback((ev) => {
    if (ev.modelType !== 'ISSUE_CLASSIFIER') return;
    if (ev.event === 'job_update' && ev.jobId === activeJobRef.current && typeof ev.progress === 'number') setTrainingProgress(ev.progress);
    if (['model_promoted', 'model_rolled_back', 'model_deleted', 'model_archived'].includes(ev.event)) fetchEcosystemData({ quiet: true });
  }, [fetchEcosystemData]));
  // --- END UPDATE

  // ─────────────────────────────────────────────────────────────────────────────
  // ACTIONS
  // ─────────────────────────────────────────────────────────────────────────────

  // --- UPDATE (ML Ops): real training run — no simulated progress, no empty payload
  const handleTrainModel = async () => {
    setIsTraining(true);
    setTrainingProgress(2);
    try {
      let job = await mlOpsService.train('ISSUE_CLASSIFIER');
      activeJobRef.current = job.id;
      toast({
        title: job.alreadyActive ? 'Already training' : 'Training started',
        description: job.alreadyActive ? 'Following the run that is already in progress.' : 'FlowSense is loading categorised work orders and issues for the ML engine.',
        status: 'info', duration: 3000, isClosable: true,
      });
      setTrainingLogs(prev => [{ timestamp: new Date(), status: 'info', message: `Run ${job.id} ${job.alreadyActive ? 'already active' : 'queued'}` }, ...prev]);

      // poll until the run finishes (websocket updates the bar in between)
      const started = Date.now();
      while (isMounted.current && ['queued', 'running'].includes(job.status) && Date.now() - started < 30 * 60 * 1000) {
        await new Promise(r => setTimeout(r, 2500));
        job = await mlOpsService.getJob(job.id);
        if (isMounted.current) setTrainingProgress(job.progress || 0);
      }
      if (!isMounted.current) return;
      setTrainingProgress(100);
      const lastLog = (job.log || []).slice(-1)[0]?.msg || job.stage;
      const ok = job.status === 'succeeded';
      toast({
        title: ok ? ({ promoted: 'New model promoted to production', awaiting_approval: 'New challenger staged — waiting for approval', kept_champion: 'Production model kept', rejected_safeguard: 'New model rejected by the safeguard' }[job.decision] || 'Training complete')
          : job.status === 'skipped' ? 'Training skipped' : 'Training failed',
        description: job.error || lastLog,
        status: ok ? (job.decision === 'promoted' ? 'success' : 'info') : job.status === 'skipped' ? 'warning' : 'error',
        duration: 7000, isClosable: true,
      });
      setTrainingLogs(prev => [{ timestamp: new Date(), status: ok ? 'success' : job.status === 'skipped' ? 'warning' : 'error', message: job.error || lastLog || job.status }, ...prev]);
      fetchEcosystemData({ quiet: true });
    } catch (error) {
      if (!isMounted.current) return;
      setTrainingProgress(0);
      toast({ title: 'Training Failed', description: error.message || 'The ML engine encountered a fatal error.', status: 'error', duration: 5000, isClosable: true });
      setTrainingLogs(prev => [{ timestamp: new Date(), status: 'error', message: error.message }, ...prev]);
    } finally {
      activeJobRef.current = null;
      if (isMounted.current) setTimeout(() => { if (isMounted.current) setIsTraining(false); }, 800);
    }
  };

  const handlePromote = async (modelVersion) => {
    setIsPromoting(modelVersion);
    try {
      // promotion flushes the ML engine cache server-side
      await mlOpsService.promote('ISSUE_CLASSIFIER', modelVersion);
      toast({ title: 'Promotion Successful', description: `Model ${modelVersion} is now serving production traffic.`, status: 'success', duration: 5000 });
      fetchEcosystemData({ quiet: true });
    } catch (err) {
      toast({ title: 'Promotion Failed', description: err.message, status: 'error', duration: 7000 });
    } finally {
      if (isMounted.current) setIsPromoting(null);
    }
  };

  const handleDeleteModel = async () => {
    if (!modelToDelete) return;
    try {
      await mlOpsService.remove('ISSUE_CLASSIFIER', modelToDelete);
      toast({ title: 'Model Deleted', description: `Staged model ${modelToDelete} removed.`, status: 'success', duration: 4000 });
      fetchEcosystemData({ quiet: true });
    } catch (err) {
      toast({ title: 'Deletion Failed', description: err.message, status: 'error', duration: 5000 });
    } finally {
      if (isMounted.current) setModelToDelete(null);
    }
  };
  // --- END UPDATE

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER HELPERS
  // ─────────────────────────────────────────────────────────────────────────────

  const renderCharts = () => {
    if (!productionMetrics?.classReport) {
      return (
        <Flex direction="column" align="center" py={10} gap={2}>
          <Icon as={FaChartBar} boxSize={8} color={textColor} opacity={0.3} />
          <Text color={textColor} fontSize="11px" letterSpacing="0.08em">NO CLASS DISTRIBUTION DATA AVAILABLE</Text>
        </Flex>
      );
    }

    const report = productionMetrics.classReport;
    // Filter out systemic keys like 'accuracy', 'macro avg', 'weighted avg'
    const classes = Object.keys(report).filter(k => typeof report[k] === 'object' && !['macro avg', 'weighted avg'].includes(k));
    
    const chartCategories = classes.map(c => c.toUpperCase());
    const f1Scores = classes.map(c => report[c]['f1-score'] * 100);

    const chartOptions = {
      chart: { type: 'bar', toolbar: { show: false }, foreColor: textColor },
      plotOptions: { bar: { horizontal: true, borderRadius: 4, distributed: true, dataLabels: { position: 'top' } } },
      xaxis: { categories: chartCategories, title: { text: 'F1-Score (%)' }, max: 100 },
      yaxis: { labels: { maxWidth: 160, style: { fontWeight: 600, fontSize: '10px' } } },
      colors: ['#805AD5', '#3182CE', '#38B2AC', '#DD6B20', '#E53E3E', '#4299E1'],
      legend: { show: false },
      dataLabels: { enabled: false },
      grid: { borderColor: borderColor, strokeDashArray: 4 }
    };

    return (
      <Box w="100%" overflowX="auto" mt={4}>
        <ReactApexChart options={chartOptions} series={[{ name: 'F1-Score', data: f1Scores }]} type="bar" height={280} />
      </Box>
    );
  };

  if (isLoading && !productionMetrics) {
    return (
      <VStack align="stretch" spacing={4}>
        <Skeleton height="40px" width="250px" />
        <SimpleGrid columns={3} spacing={4}>
          <Skeleton height="100px" />
          <Skeleton height="100px" />
          <Skeleton height="100px" />
        </SimpleGrid>
        <Skeleton height="200px" />
      </VStack>
    );
  }

  return (
    <Box>
      <Flex justify="space-between" align="flex-start" mb={4}>
        <HStack spacing={3}>
          <Box p={2.5} bg="purple.50" borderRadius="xl" color="purple.500" borderWidth="1px" borderColor="purple.200">
            <Icon as={FaProjectDiagram} boxSize={5} />
          </Box>
          <VStack align="flex-start" spacing={0}>
            <Heading size="md" color={headingColor} bgGradient="linear(to-r, purple.600, blue.500)" bgClip="text">
              Issue Classifier NLP
            </Heading>
            <Text fontSize="10px" color={textColor} letterSpacing="0.08em" textTransform="uppercase">
              Deep Learning Triage & Categorization
            </Text>
          </VStack>
        </HStack>
        <HStack spacing={2}>
          {/* --- UPDATE (ML Ops) */}
          <ExplainButton topic="ISSUE_CLASSIFIER" />
          {/* --- END UPDATE */}
          <Badge colorScheme={productionMetrics?.accuracy > 0.85 ? 'green' : 'yellow'} px={3} py={1.5} borderRadius="full" fontSize="10px" letterSpacing="0.06em">
            {isTraining ? 'TRAINING…' : (productionMetrics ? 'PRODUCTION ACTIVE' : 'NO MODEL')}
          </Badge>
        </HStack>
      </Flex>

      <Tabs variant="unstyled" colorScheme="purple">
        <TabList bg={tabBg} borderRadius="xl" p={1} mb={5} display="flex" flexWrap="wrap" gap={1}>
          {[
            { label: 'Production Model', icon: FaCheckCircle },
            { label: `Staged Models (${stagedModels.length})`, icon: FaHourglassHalf },
            { label: 'Training Logs', icon: FaHistory },
          ].map((t) => (
            <Tab key={t.label} flex="1" borderRadius="lg" py={2} px={3} fontSize="10px" fontWeight="800" letterSpacing="0.06em" color={textColor} transition="all 0.2s" _selected={{ bg: tabActiveBg, color: 'purple.500', boxShadow: 'sm' }} _hover={{ color: 'purple.400' }}>
              <HStack spacing={2} justify="center">
                <Icon as={t.icon} boxSize={3.5} />
                <Text whiteSpace="nowrap">{t.label}</Text>
              </HStack>
            </Tab>
          ))}
        </TabList>

        <TabPanels>
          {/* TAB 1: PRODUCTION */}
          <TabPanel px={0} pb={0}>
            <motion.div variants={STAGGER_CONTAINER} initial="hidden" animate="visible">
              
              {!productionMetrics ? (
                <Alert status="warning" borderRadius="xl"><AlertIcon />No active production model found for Issue Classification.</Alert>
              ) : (
                <>
                  <SimpleGrid columns={{ base: 1, md: 2, '2xl': 4 }} spacing={4} mb={6}>
                    <MetricCard label="Model Version" value={`v${productionMetrics.modelVersion || '1.0'}`} helpText={new Date(productionMetrics.lastTrained).toLocaleDateString()} accent="#805AD5" icon={FaShieldAlt} />
                    <MetricCard label="Global Accuracy" value={`${(productionMetrics.accuracy * 100).toFixed(1)}%`} helpText="Validation Set" accent={productionMetrics.accuracy > 0.85 ? '#38B2AC' : '#E53E3E'} icon={MdFactCheck} isHealthy={productionMetrics.accuracy > 0.85} />
                    <MetricCard label="Macro Precision" value={`${(productionMetrics.precision * 100).toFixed(1)}%`} helpText="False Positive Resistance" accent="#3182CE" icon={MdPrecisionManufacturing} />
                    <MetricCard label="Macro F1-Score" value={`${(productionMetrics.f1Score * 100).toFixed(1)}%`} helpText="Harmonic Mean" accent="#DD6B20" icon={MdTrendingUp} />
                  </SimpleGrid>

                  <Box mb={6} borderWidth="1px" borderColor={borderColor} borderRadius="xl" p={5} bg={cardBg}>
                    <HStack mb={2} spacing={2}>
                      <Icon as={FaChartBar} color="purple.400" />
                      <Text fontSize="sm" fontWeight="800" color={headingColor}>Class Detection Performance</Text>
                    </HStack>
                    {renderCharts()}
                  </Box>
                </>
              )}

              <Divider mb={4} />

              <VStack align="stretch" spacing={3}>
                {isTraining && (
                  <Box p={4} bg="purple.50" borderRadius="xl" borderWidth="1px" borderColor="purple.200">
                    <Flex justify="space-between" mb={2}>
                      <Text fontSize="xs" fontWeight="bold" color="purple.600">Backpropagating Gradients & Optimizing Weights...</Text>
                      <Text fontSize="xs" color="purple.600" fontWeight="bold">{trainingProgress.toFixed(0)}%</Text>
                    </Flex>
                    <Progress value={trainingProgress} size="sm" colorScheme="purple" borderRadius="full" hasStripe isAnimated />
                  </Box>
                )}
                <Button
                  colorScheme="purple"
                  leftIcon={isTraining ? <FaBrain /> : <FaPlay />}
                  onClick={handleTrainModel}
                  isLoading={isTraining}
                  loadingText="Training Model..."
                  w="full"
                  variant={isTraining ? "outline" : "solid"}
                  boxShadow="md"
                  _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                >
                  Trigger Retraining Pipeline
                </Button>
              </VStack>
            </motion.div>
          </TabPanel>

          {/* TAB 2: STAGED MODELS */}
          <TabPanel px={0} pb={0}>
            <VStack align="stretch" spacing={4}>
              {stagedModels.length === 0 ? (
                <Alert status="info" borderRadius="xl" variant="subtle">
                  <AlertIcon />
                  <Box>
                    <AlertTitle fontSize="11px" letterSpacing="0.08em">NO STAGED MODELS</AlertTitle>
                    <AlertDescription fontSize="11px">Trigger the retraining pipeline to generate a new challenger model.</AlertDescription>
                  </Box>
                </Alert>
              ) : (
                <motion.div variants={STAGGER_CONTAINER} initial="hidden" animate="visible">
                  <VStack spacing={3} align="stretch">
                    {stagedModels.map((model) => (
                      <MotionBox key={model.model_version} variants={LIST_ITEM_VARIANT}>
                        <Box p={4} borderWidth={1} borderColor={borderColor} borderRadius="xl" bg={listItemBg} _hover={{ borderColor: 'purple.400', shadow: 'md' }} transition="all 0.2s">
                          <Flex justify="space-between" align="flex-start" wrap="wrap" gap={3}>
                            <Box flex="1">
                              <HStack mb={2}>
                                <Badge colorScheme="purple">v{model.model_version}</Badge>
                                <Text fontSize="10px" color={textColor}>{new Date(model.created_at).toLocaleString()}</Text>
                              </HStack>
                              <HStack spacing={4} mt={2}>
                                <Stat>
                                  <StatLabel fontSize="9px" color={textColor}>ACCURACY</StatLabel>
                                  <StatNumber fontSize="md">{(model.parsedMetrics?.accuracy * 100).toFixed(1)}%</StatNumber>
                                </Stat>
                                <Stat>
                                  <StatLabel fontSize="9px" color={textColor}>F1-SCORE</StatLabel>
                                  <StatNumber fontSize="md">{(model.parsedMetrics?.f1Score * 100).toFixed(1)}%</StatNumber>
                                </Stat>
                              </HStack>
                            </Box>
                            <VStack spacing={2} minW="150px">
                              <Button size="xs" colorScheme="green" w="full" leftIcon={<ArrowUpIcon />} isLoading={isPromoting === model.model_version} onClick={() => handlePromote(model.model_version)}>
                                PROMOTE
                              </Button>
                              <HStack w="full">
                                <Button size="xs" variant="outline" flex="1" leftIcon={<InfoIcon />} onClick={() => { setSelectedComparison(model); setIsComparisonOpen(true); }} isDisabled={!productionMetrics}>
                                  COMPARE
                                </Button>
                                <Tooltip label="Delete staged model" hasArrow>
                                  <IconButton size="xs" colorScheme="red" variant="outline" icon={<DeleteIcon />} onClick={() => setModelToDelete(model.model_version)} aria-label="Delete" />
                                </Tooltip>
                              </HStack>
                            </VStack>
                          </Flex>
                        </Box>
                      </MotionBox>
                    ))}
                  </VStack>
                </motion.div>
              )}
            </VStack>
          </TabPanel>

          {/* TAB 3: LOGS */}
          <TabPanel px={0} pb={0}>
            <VStack align="stretch" spacing={3}>
              <AnimatePresence mode="popLayout">
                {trainingLogs.length > 0 ? (
                  trainingLogs.map((log, idx) => (
                    <MotionBox key={idx} variants={LIST_ITEM_VARIANT} initial="hidden" animate="visible" exit="exit">
                      <Box p={3} borderWidth="1px" borderColor={log.status === 'success' ? 'green.200' : 'red.200'} borderRadius="xl" bg={listItemBg} position="relative" overflow="hidden">
                        <Box position="absolute" left={0} top={0} bottom={0} w="3px" bg={log.status === 'success' ? '#34d399' : '#ef4444'} />
                        <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
                          <HStack spacing={3} pl={2}>
                            <Icon as={log.status === 'success' ? FaCheckCircle : FaExclamationTriangle} color={log.status === 'success' ? 'green.400' : 'red.400'} boxSize={3.5} />
                            <VStack spacing={0} align="flex-start">
                              <Text fontSize="11px" fontWeight="700">{log.message}</Text>
                              <Text fontSize="9px" color={textColor}>{log.status.toUpperCase()}</Text>
                            </VStack>
                          </HStack>
                          <Text fontSize="9px" color={textColor}>{log.timestamp.toLocaleString()}</Text>
                        </Flex>
                      </Box>
                    </MotionBox>
                  ))
                ) : (
                  <Alert status="info" borderRadius="xl"><AlertIcon /><Text fontSize="11px">No training logs generated in this session.</Text></Alert>
                )}
              </AnimatePresence>
            </VStack>
          </TabPanel>

        </TabPanels>
      </Tabs>

      {/* COMPARISON MODAL */}
      <ModelComparisonModal isOpen={isComparisonOpen} onClose={() => setIsComparisonOpen(false)} productionModel={productionMetrics} stagedModel={selectedComparison} />

      {/* DELETE CONFIRMATION DIALOG */}
      <AlertDialog isOpen={!!modelToDelete} onClose={() => setModelToDelete(null)}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">Delete Staged Model</AlertDialogHeader>
            <AlertDialogBody>
              Are you sure? Model version <Code>{modelToDelete}</Code> will be permanently deleted from the registry.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button onClick={() => setModelToDelete(null)}>Cancel</Button>
              <Button colorScheme="red" onClick={handleDeleteModel} ml={3}>Delete</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

    </Box>
  );
};

export default memo(IssueClassifierTrainingWidget);